import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Accordion,
    Alert,
    Button,
    Col,
    Container,
    Form,
    Modal,
    OverlayTrigger,
    Row,
    Table,
    Tooltip,
} from "react-bootstrap";
import axios from "axios";
import Cookies from "js-cookie";
import Select from "react-select";
import {
    BsCheck,
    BsExclamationTriangleFill,
    BsGripVertical,
    BsInfoCircle,
    BsPlusLg,
    BsTrash3,
} from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { fetchDepartments, fetchUsers } from "../../../redux/actions";
import { baseURL } from "../../../helpers/baseURL";
import { ucFirst } from "../../../helpers/generic";
import MainContainer from "../../MainContainer";
import EmbedFormConfirmModal from "./EmbedFormConfirmModal";
import { useEmbedFormDialogs } from "./useEmbedFormDialogs";
import { colorOption, colorValueSingle, imageOption } from "../../SelectComponents";
import EmbedFormEditorPreview, {
    parseNumericPxFromEmbedWidth,
} from "./EmbedFormEditorPreview";
import EmbedHowToModal from "./EmbedHowToModal";
import EmbedTrustedDomainPicker from "./EmbedTrustedDomainPicker";
import {
    EMBED_DEFAULT_BUTTON_COLOR,
    EMBED_DISPLAY_MODE_FLOATING,
    EMBED_DISPLAY_MODE_INLINE,
    EMBED_LAUNCHER_POSITION_OPTIONS,
    EMBED_SUCCESS_FONT_FAMILY_OPTIONS,
    EMBED_SUCCESS_FONT_SIZE_OPTIONS,
    EMBED_SUCCESS_FONT_WEIGHT_OPTIONS,
    EMBED_DEFAULT_SUCCESS_DISCLAIMER,
    embedFieldChrome,
    normalizeEmbedAlign,
    normalizeEmbedOptionsLayout,
    resolveEmbedSupportContact,
} from "../../../embedTicketFormTheme";
import "./embed-form-editor.css";
import { EmbedFormBtn } from "./embedFormButtons";
import EmbedDepartmentLabelHint from "./EmbedDepartmentLabelHint";
import { handleEmbedAuthRedirect } from "./embedAuthRedirect";

const EMBED_ALIGN_OPTIONS = ["left", "center", "right"];

const EMBED_FORM_DISCARD_CONFIRM_MESSAGE =
    "You have unsaved changes. Discard them and leave this page?";

function blockedNavigationPath(location) {
    if (!location || typeof location !== "object") {
        return null;
    }
    return (
        (location.pathname || "/") +
        (location.search || "") +
        (location.hash || "")
    );
}

function EmbedAlignButtonGroup({ value, onChange }) {
    return (
        <div className="d-flex flex-wrap justify-content-around gap-2 embed-form-editor-label-align-btns">
            {EMBED_ALIGN_OPTIONS.map((align) => (
                <Button
                    key={align}
                    type="button"
                    variant={
                        value === align
                            ? EmbedFormBtn.primary
                            : EmbedFormBtn.neutral
                    }
                    onClick={() => onChange(align)}
                >
                    {align === "left"
                        ? "Left"
                        : align === "center"
                          ? "Center"
                          : "Right"}
                </Button>
            ))}
        </div>
    );
}

const authHeaders = () => ({
    Authorization: `Bearer ${Cookies.get("access_token")}`,
});

let customFieldClientIdCounter = 0;

function createCustomFieldClientId() {
    customFieldClientIdCounter += 1;
    return `embed-cf-${customFieldClientIdCounter}`;
}

function customFieldFromApi(f) {
    return {
        _clientId: createCustomFieldClientId(),
        label: f.label || "",
        name: f.name || "",
        placeholder: f.placeholder || "",
        type: f.type || "text",
        required: !!f.required,
        options: Array.isArray(f.options) ? f.options : [],
        options_layout: embedFieldTypeUsesOptionsLayout(f.type || "text")
            ? normalizeEmbedOptionsLayout(f.options_layout)
            : undefined,
    };
}

const emptyField = () => ({
    _clientId: createCustomFieldClientId(),
    label: "",
    name: "",
    placeholder: "",
    type: "text",
    required: false,
    options: [],
});

/** Stable #rrggbb for <input type="color" /> when text may be invalid */
function colorPickerValue(text, fallback) {
    if (typeof text !== "string") return fallback;
    const s = text.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s;
    if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
        return (
            "#" +
            s[1] +
            s[1] +
            s[2] +
            s[2] +
            s[3] +
            s[3]
        ).toLowerCase();
    }
    return fallback;
}

function normalizedOptionsForSave(options) {
    if (!Array.isArray(options)) return [];
    return options.map((s) => String(s).trim()).filter((s) => s.length > 0);
}

function embedFieldTypeUsesOptions(type) {
    return type === "select" || type === "radio" || type === "checkbox";
}

function embedFieldTypeShowsPlaceholderInput(type) {
    return (
        type !== "radio" && type !== "checkbox" && type !== "birthday"
    );
}

function embedFieldTypeUsesOptionsLayout(type) {
    return type === "radio" || type === "checkbox";
}

function customFieldOptionsLayoutForSave(field) {
    if (!embedFieldTypeUsesOptionsLayout(field.type)) {
        return undefined;
    }
    return normalizeEmbedOptionsLayout(field.options_layout);
}

const EMBED_FORM_DEFAULT_SUCCESS_MESSAGE =
    "Thank you for contacting support!";

/** Default values for the Design wizard step (colors, layout, custom CSS). */
function nowMs() {
    return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/**
 * Throttle a setter so its caller (e.g. `<input type="color">`'s `onChange`, which fires
 * on every pixel of a drag) only triggers a React state update at most once per `ms`.
 * Leading-edge fires immediately; intermediate calls update the latest queued value;
 * a trailing call flushes once the window elapses. Keeps the editor responsive while
 * the native picker keeps drawing its own UI live.
 */
function useThrottledCallback(fn, ms = 80) {
    const lastFlushAtRef = useRef(0);
    const timeoutRef = useRef(null);
    const latestArgRef = useRef(undefined);
    const fnRef = useRef(fn);

    useEffect(() => {
        fnRef.current = fn;
    });

    useEffect(() => {
        return () => {
            if (timeoutRef.current != null) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    return useCallback(
        (value) => {
            latestArgRef.current = value;
            const now = nowMs();
            const elapsed = now - lastFlushAtRef.current;
            if (elapsed >= ms) {
                lastFlushAtRef.current = now;
                fnRef.current(value);
                return;
            }
            if (timeoutRef.current != null) {
                return;
            }
            const remaining = ms - elapsed;
            timeoutRef.current = window.setTimeout(() => {
                timeoutRef.current = null;
                lastFlushAtRef.current = nowMs();
                fnRef.current(latestArgRef.current);
            }, remaining);
        },
        [ms]
    );
}

const EMBED_FORM_DESIGN_INITIAL = {
    backgroundColor: "#ffffff",
    textColor: "#212529",
    fieldBackgroundColor: "",
    buttonText: "Submit",
    buttonColor: "",
    buttonTextColor: "#ffffff",
    formMaxWidth: "480",
    formMinWidth: "",
    formBorderRadius: 8,
    customCss: "",
    labelAlign: "left",
    titleAlign: "left",
    subtitleAlign: "left",
};

const EMBED_FORM_DISPLAY_INITIAL = {
    displayMode: EMBED_DISPLAY_MODE_INLINE,
    launcherPosition: "bottom-right",
    launcherColor: "",
};

const EMBED_SUCCESS_DESIGN_INITIAL = {
    successMessageColor: "#212529",
    successMessageFontFamily: "poppins",
    successMessageFontSize: "1.25rem",
    successMessageFontWeight: "600",
};

/** Normalized shape aligned with `submit` payload for dirty detection */
function buildEmbedFormPersistSnapshot(fields) {
    const {
        name,
        title,
        subtitle,
        titleColor,
        subtitleColor,
        isActive,
        trustedDomainIds,
        departmentId,
        backgroundColor,
        textColor,
        fieldBackgroundColor,
        buttonText,
        buttonColor,
        buttonTextColor,
        formMaxWidth,
        formMinWidth,
        formBorderRadius,
        successMessage,
        successDisclaimer,
        supportEmail,
        notifyEmailsText,
        autoAssignUserIds,
        customFields,
        customCss,
        labelAlign,
        titleAlign,
        subtitleAlign,
        successMessageColor,
        successMessageFontFamily,
        successMessageFontSize,
        successMessageFontWeight,
        displayMode,
        launcherPosition,
        launcherColor,
    } = fields;
    const trusted_domain_ids = [...(trustedDomainIds || [])].map(Number);
    const notification_emails = notifyEmailsText
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    return {
        name,
        title: title || null,
        subtitle: subtitle || null,
        title_color:
            String(title ?? "").trim() && (titleColor ?? "").trim()
                ? (titleColor ?? "").trim()
                : null,
        subtitle_color:
            String(subtitle ?? "").trim() && (subtitleColor ?? "").trim()
                ? (subtitleColor ?? "").trim()
                : null,
        is_active: isActive,
        trusted_domain_ids,
        department_id: Number(departmentId),
        background_color: backgroundColor,
        text_color: textColor || null,
        field_background_color: (fieldBackgroundColor ?? "").trim() || null,
        button_text: buttonText,
        button_color: buttonColor || null,
        button_text_color: buttonTextColor || null,
        form_max_width: formMaxWidth || null,
        form_min_width: formMinWidth || null,
        form_border_radius:
            formBorderRadius != null && formBorderRadius !== ""
                ? Math.min(64, Math.max(0, Number(formBorderRadius)))
                : null,
        custom_css: (customCss ?? "").trim() || null,
        label_align: normalizeEmbedAlign(labelAlign),
        title_align: normalizeEmbedAlign(titleAlign),
        subtitle_align: normalizeEmbedAlign(subtitleAlign),
        success_message: successMessage,
        success_disclaimer: (successDisclaimer ?? "").trim() || null,
        support_email: (supportEmail ?? "").trim() || null,
        success_message_color: (successMessageColor ?? "").trim() || null,
        success_message_font_family:
            (successMessageFontFamily ?? "").trim() || null,
        success_message_font_size:
            (successMessageFontSize ?? "").trim() || null,
        success_message_font_weight:
            (successMessageFontWeight ?? "").trim() || null,
        success_checklist_loop: false,
        display_mode:
            displayMode === EMBED_DISPLAY_MODE_FLOATING
                ? EMBED_DISPLAY_MODE_FLOATING
                : EMBED_DISPLAY_MODE_INLINE,
        ...(displayMode === EMBED_DISPLAY_MODE_FLOATING
            ? {
                  launcher_position: launcherPosition || "bottom-right",
                  launcher_color: (launcherColor ?? "").trim() || null,
              }
            : {}),
        notification_emails,
        auto_assign_user_ids: [...(autoAssignUserIds || [])]
            .map(Number)
            .filter((id) => id > 0)
            .sort((a, b) => a - b),
        custom_fields: customFields.map((f) => {
            const row = {
                label: f.label ?? "",
                name: f.name || undefined,
                placeholder: f.placeholder || "",
                type: f.type,
                required: f.required,
                options: embedFieldTypeUsesOptions(f.type)
                    ? normalizedOptionsForSave(f.options)
                    : [],
            };
            const layout = customFieldOptionsLayoutForSave(f);
            if (layout !== undefined) {
                row.options_layout = layout;
            }
            return row;
        }),
    };
}

function newEmbedFormBaselineSnapshotJson() {
    return JSON.stringify(
        buildEmbedFormPersistSnapshot({
            name: "",
            title: "",
            subtitle: "",
            titleColor: "",
            subtitleColor: "",
            isActive: true,
            trustedDomainIds: [],
            departmentId: "",
            ...EMBED_FORM_DESIGN_INITIAL,
            ...EMBED_FORM_DISPLAY_INITIAL,
            ...EMBED_SUCCESS_DESIGN_INITIAL,
            successMessage: EMBED_FORM_DEFAULT_SUCCESS_MESSAGE,
            successDisclaimer: EMBED_DEFAULT_SUCCESS_DISCLAIMER,
            supportEmail: "",
            notifyEmailsText: "",
            autoAssignUserIds: [],
            customFields: [],
        })
    );
}

function parseTrustedDomainsForWizard(trustedDomainIds) {
    return Array.isArray(trustedDomainIds)
        ? trustedDomainIds.map(Number).filter((n) => n > 0)
        : [];
}

function customFieldsLabelsValidForWizard(customFields) {
    if (!Array.isArray(customFields) || customFields.length === 0) {
        return true;
    }
    return customFields.every(
        (f) => String(f.label ?? "").trim().length > 0
    );
}

function customFieldsOptionsValidForWizard(customFields) {
    if (!Array.isArray(customFields)) {
        return true;
    }
    for (const f of customFields) {
        if (!embedFieldTypeUsesOptions(f.type)) {
            continue;
        }
        const opts = normalizedOptionsForSave(f.options || []);
        if (opts.length === 0) {
            return false;
        }
    }
    return true;
}

function wizardStepSatisfied(step, ctx) {
    switch (step) {
        case 0:
            return (
                ctx.name.trim().length > 0 && Boolean(ctx.departmentId)
            );
        case 1:
            return parseTrustedDomainsForWizard(ctx.trustedDomainIds).length > 0;
        case 2:
            return (
                customFieldsLabelsValidForWizard(ctx.customFields) &&
                customFieldsOptionsValidForWizard(ctx.customFields)
            );
        case 3:
            return true;
        case 4: {
            if (!ctx.successMessage.trim()) {
                return false;
            }
            const contact = ctx.supportEmail.trim();
            if (!contact) {
                return true;
            }
            return resolveEmbedSupportContact(contact) !== null;
        }
        case 5:
            return true;
        default:
            return true;
    }
}

function wizardStepErrorMessage(step, ctx) {
    switch (step) {
        case 0:
            return "Enter a form name and choose a department before continuing.";
        case 1:
            return "Add at least one trusted domain before continuing.";
        case 2:
            if (
                ctx &&
                Array.isArray(ctx.customFields) &&
                ctx.customFields.length > 0 &&
                !customFieldsLabelsValidForWizard(ctx.customFields)
            ) {
                return "Every custom field needs a label (this is what visitors see on the form).";
            }
            return "Each select, radio, and checkbox field needs at least one option.";
        case 3:
            return "Complete this step before continuing.";
        case 4:
            if (!ctx.successMessage.trim()) {
                return "Enter a success message before continuing.";
            }
            if (
                ctx.supportEmail.trim() &&
                !resolveEmbedSupportContact(ctx.supportEmail.trim())
            ) {
                return "Enter a valid email, phone number, or link, or leave it blank.";
            }
            return "Complete this step before continuing.";
        default:
            return "Complete this step before continuing.";
    }
}

/** Human-readable labels for Laravel validation keys shown on save. */
const EMBED_FORM_VALIDATION_FIELD_LABELS = {
    name: "Form name",
    department_id: "Department",
    support_email: "Contact",
    success_message: "Success message",
    success_disclaimer: "Disclaimer",
};

/** Map Laravel validation errors to a message and wizard step for inline display. */
function parseEmbedFormSaveValidationError(err) {
    const data = err?.response?.data;
    if (!data) {
        return { message: "Save failed.", step: null };
    }

    const errors = data.errors;
    if (!errors || typeof errors !== "object") {
        return {
            message: data.message || "Save failed.",
            step: null,
        };
    }

    const lines = [];
    let step = null;

    const stepForKey = (key) => {
        if (key.startsWith("custom_fields.")) {
            return 2;
        }
        if (key.startsWith("trusted_domain")) {
            return 1;
        }
        if (key === "name" || key === "department_id") {
            return 0;
        }
        if (
            key.startsWith("success_message") ||
            key === "success_disclaimer" ||
            key === "support_email"
        ) {
            return 4;
        }
        if (key.startsWith("auto_assign_user")) {
            return 3;
        }
        if (
            key.startsWith("background_") ||
            key.startsWith("button_") ||
            key.startsWith("form_") ||
            key.startsWith("display_") ||
            key.startsWith("launcher_") ||
            key === "custom_css" ||
            key.startsWith("label_align") ||
            key.startsWith("title_align")
        ) {
            return 5;
        }
        return null;
    };

    for (const [key, raw] of Object.entries(errors)) {
        const msg = Array.isArray(raw) ? raw[0] : String(raw);
        const row = key.match(/^custom_fields\.(\d+)\.(\w+)$/);
        if (row) {
            const fieldNum = Number(row[1]) + 1;
            const attr = row[2].replace(/_/g, " ");
            lines.push(`Custom field ${fieldNum} (${attr}): ${msg}`);
        } else {
            const label =
                EMBED_FORM_VALIDATION_FIELD_LABELS[key] ??
                key.replace(/_/g, " ");
            lines.push(`${label}: ${msg}`);
        }
        const s = stepForKey(key);
        if (s !== null && step === null) {
            step = s;
        }
    }

    return {
        message:
            lines.length > 0
                ? lines.join(" ")
                : data.message || "The given data was invalid.",
        step,
    };
}

function FieldOptionsEditor({
    options,
    onChange,
    fieldType,
    optionsLayout,
    onOptionsLayoutChange,
}) {
    const list = Array.isArray(options) ? options : [];
    const showLayoutToggle = embedFieldTypeUsesOptionsLayout(fieldType);
    const layout = normalizeEmbedOptionsLayout(optionsLayout);

    const updateAt = (i, value) => {
        const next = list.map((o, j) => (j === i ? value : o));
        onChange(next);
    };

    const addRow = () => {
        onChange([...list, ""]);
    };

    const removeAt = (i) => {
        onChange(list.filter((_, j) => j !== i));
    };

    return (
        <div className="embed-field-options-editor">
            {list.map((opt, i) => (
                <div key={i} className="embed-field-options-editor__row">
                    <Form.Control
                        size="sm"
                        type="text"
                        value={opt}
                        onChange={(e) => updateAt(i, e.target.value)}
                        placeholder={`Option ${i + 1}`}
                        className="embed-field-options-editor__input"
                    />
                    <Button
                        type="button"
                        variant={EmbedFormBtn.danger}
                        size="sm"
                        className="embed-field-options-editor__remove"
                        onClick={() => removeAt(i)}
                        disabled={list.length <= 1}
                        aria-label="Remove option"
                    >
                        <BsTrash3 />
                    </Button>
                </div>
            ))}
            <div className="embed-field-options-editor__actions">
                <Button
                    type="button"
                    variant={EmbedFormBtn.secondary}
                    size="sm"
                    onClick={addRow}
                >
                    Add option
                </Button>
                {showLayoutToggle && onOptionsLayoutChange ? (
                    <Button
                        type="button"
                        variant={
                            layout === "inline"
                                ? EmbedFormBtn.primary
                                : EmbedFormBtn.neutral
                        }
                        size="sm"
                        className="embed-field-options-editor__layout-toggle"
                        onClick={() =>
                            onOptionsLayoutChange(
                                layout === "inline" ? "vertical" : "inline"
                            )
                        }
                        title={
                            layout === "inline"
                                ? "Stack options vertically (one per line)"
                                : "Arrange options inline (wrap on one row)"
                        }
                    >
                        {layout === "inline"
                            ? "Stack vertically"
                            : "Arrange inline"}
                    </Button>
                ) : null}
            </div>
        </div>
    );
}

const EMBED_FORM_WIZARD_STEPS = [
    { key: "general", label: "General" },
    { key: "domains", label: "Domains" },
    { key: "fields", label: "Custom fields" },
    { key: "auto-assign", label: "Auto-Assign" },
    { key: "after", label: "After submit" },
    { key: "design", label: "Design" },
];

/** Wizard step key for auto-assigning tickets to workspace users. */
export const EMBED_FORM_WIZARD_AUTO_ASSIGN_STEP_KEY = "auto-assign";

/** Wizard step key for post-submit settings (success message, notifications). */
export const EMBED_FORM_WIZARD_AFTER_STEP_KEY = "after";

/** Wizard step key for visual design (form + after-submit styling). */
export const EMBED_FORM_WIZARD_DESIGN_STEP_KEY = "design";

/** @param {string} stepKey @returns {number} index in {@link EMBED_FORM_WIZARD_STEPS}, or -1 */
export function embedWizardStepIndex(stepKey) {
    return EMBED_FORM_WIZARD_STEPS.findIndex((s) => s.key === stepKey);
}

function RequiredFieldLabel({ children }) {
    return (
        <>
            {children}
            {" "}
            <span className="text-danger" aria-hidden="true">
                *
            </span>
        </>
    );
}

function EmbedFormWizardStepper({ steps, activeIndex, onStepClick }) {
    return (
        <nav
            className="embed-form-editor-stepper"
            aria-label="Embed form setup steps"
        >
            <div className="embed-form-editor-stepper__track">
                {steps.map((s, i) => (
                    <React.Fragment key={s.key}>
                        <button
                            type="button"
                            className={
                                "embed-form-editor-stepper__node " +
                                (i < activeIndex
                                    ? "is-done"
                                    : i === activeIndex
                                      ? "is-current"
                                      : "is-upcoming")
                            }
                            onClick={() => onStepClick(i)}
                            aria-current={
                                i === activeIndex ? "step" : undefined
                            }
                        >
                            <span className="embed-form-editor-stepper__ring">
                                <span className="embed-form-editor-stepper__bubble">
                                    {i < activeIndex ? (
                                        <BsCheck aria-hidden size={16} />
                                    ) : (
                                        <span>{i + 1}</span>
                                    )}
                                </span>
                                {s.warning ? (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip
                                                id={`embed-stepper-warning-${s.key}`}
                                            >
                                                {s.warning}
                                            </Tooltip>
                                        }
                                    >
                                        <span
                                            className="embed-form-editor-stepper__warning"
                                            role="img"
                                            aria-label={s.warning}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <BsExclamationTriangleFill
                                                aria-hidden
                                            />
                                        </span>
                                    </OverlayTrigger>
                                ) : null}
                            </span>
                            <span className="embed-form-editor-stepper__label">
                                {s.label}
                            </span>
                        </button>
                        {i < steps.length - 1 ? (
                            <span
                                className={
                                    "embed-form-editor-stepper__bar " +
                                    (i < activeIndex ? "is-done" : "")
                                }
                                aria-hidden
                            />
                        ) : null}
                    </React.Fragment>
                ))}
            </div>
        </nav>
    );
}

const EmbedTicketFormEditor = () => {
    const { id } = useParams();
    const isNew = !id;
    const history = useHistory();
    const dispatch = useDispatch();
    const departments = useSelector((s) => s.departmentsReducer.departments);
    const workspaceUsers = useSelector((s) => s.usersReducer.users) || [];
    const { confirmDialog, alertDialog, dialogNodes } = useEmbedFormDialogs();

    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
    const skipNavigationBlockRef = useRef(false);
    const pendingNavigationRef = useRef(null);
    const leavePromptOpenRef = useRef(false);
    const navigationUnblockRef = useRef(null);
    const confirmingLeaveRef = useRef(false);
    const authCheckInFlightRef = useRef(false);
    const draggedFieldIndexRef = useRef(null);
    const [dragOverFieldIndex, setDragOverFieldIndex] = useState(null);
    const [name, setName] = useState("");
    const [title, setTitle] = useState("");
    const [subtitle, setSubtitle] = useState("");
    const [titleColor, setTitleColor] = useState("");
    const [subtitleColor, setSubtitleColor] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [displayMode, setDisplayMode] = useState(
        EMBED_FORM_DISPLAY_INITIAL.displayMode
    );
    const [launcherPosition, setLauncherPosition] = useState(
        EMBED_FORM_DISPLAY_INITIAL.launcherPosition
    );
    const [launcherColor, setLauncherColor] = useState(
        EMBED_FORM_DISPLAY_INITIAL.launcherColor
    );
    const [trustedDomainIds, setTrustedDomainIds] = useState([]);
    const [trustedDomainOptions, setTrustedDomainOptions] = useState(null);
    const [missingTrustedDomains, setMissingTrustedDomains] = useState([]);
    const [departmentId, setDepartmentId] = useState("");
    const [backgroundColor, setBackgroundColor] = useState(
        EMBED_FORM_DESIGN_INITIAL.backgroundColor
    );
    const [textColor, setTextColor] = useState(
        EMBED_FORM_DESIGN_INITIAL.textColor
    );
    const [fieldBackgroundColor, setFieldBackgroundColor] = useState(
        EMBED_FORM_DESIGN_INITIAL.fieldBackgroundColor
    );
    const [buttonText, setButtonText] = useState(
        EMBED_FORM_DESIGN_INITIAL.buttonText
    );
    const [buttonColor, setButtonColor] = useState(
        EMBED_FORM_DESIGN_INITIAL.buttonColor
    );
    const [buttonTextColor, setButtonTextColor] = useState(
        EMBED_FORM_DESIGN_INITIAL.buttonTextColor
    );
    const [formMaxWidth, setFormMaxWidth] = useState(
        EMBED_FORM_DESIGN_INITIAL.formMaxWidth
    );
    const [formMinWidth, setFormMinWidth] = useState(
        EMBED_FORM_DESIGN_INITIAL.formMinWidth
    );
    const [formBorderRadius, setFormBorderRadius] = useState(
        EMBED_FORM_DESIGN_INITIAL.formBorderRadius
    );
    const [customCss, setCustomCss] = useState(
        EMBED_FORM_DESIGN_INITIAL.customCss
    );
    const [labelAlign, setLabelAlign] = useState(
        EMBED_FORM_DESIGN_INITIAL.labelAlign
    );
    const [titleAlign, setTitleAlign] = useState(
        EMBED_FORM_DESIGN_INITIAL.titleAlign
    );
    const [subtitleAlign, setSubtitleAlign] = useState(
        EMBED_FORM_DESIGN_INITIAL.subtitleAlign
    );
    const [successMessageColor, setSuccessMessageColor] = useState(
        EMBED_SUCCESS_DESIGN_INITIAL.successMessageColor
    );
    const [successMessageFontFamily, setSuccessMessageFontFamily] = useState(
        EMBED_SUCCESS_DESIGN_INITIAL.successMessageFontFamily
    );
    const [successMessageFontSize, setSuccessMessageFontSize] = useState(
        EMBED_SUCCESS_DESIGN_INITIAL.successMessageFontSize
    );
    const [successMessageFontWeight, setSuccessMessageFontWeight] = useState(
        EMBED_SUCCESS_DESIGN_INITIAL.successMessageFontWeight
    );
    const [designPreviewView, setDesignPreviewView] = useState("form");
    const [afterSubmitPreviewModalOpen, setAfterSubmitPreviewModalOpen] =
        useState(false);
    const [advancedAccordionKey, setAdvancedAccordionKey] = useState(null);
    const [successMessage, setSuccessMessage] = useState(
        EMBED_FORM_DEFAULT_SUCCESS_MESSAGE
    );
    const [successDisclaimer, setSuccessDisclaimer] = useState(
        EMBED_DEFAULT_SUCCESS_DISCLAIMER
    );
    const [supportEmail, setSupportEmail] = useState("");
    const [notifyEmailsText, setNotifyEmailsText] = useState("");
    const [autoAssignUserIds, setAutoAssignUserIds] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [wizardStep, setWizardStep] = useState(0);
    const [wizardError, setWizardError] = useState(null);
    /** Avoid double-activation when Next is replaced by Finish/Save in the same layout slot */
    const [lastStepSaveEnabled, setLastStepSaveEnabled] = useState(false);
    /** JSON string from `buildEmbedFormPersistSnapshot` after load (edit) or new-form defaults */
    const [persistBaselineJson, setPersistBaselineJson] = useState(null);
    const [showEmbedCssHelp, setShowEmbedCssHelp] = useState(false);
    /** Set after successful create — modal explains embedding before returning to the list */
    const [createdEmbedInfo, setCreatedEmbedInfo] = useState(null);

    const wizardLastIndex = EMBED_FORM_WIZARD_STEPS.length - 1;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(
                    `${baseURL}ticket-embed-trusted-domains`,
                    {
                        headers: {
                            Authorization: `Bearer ${Cookies.get(
                                "access_token"
                            )}`,
                        },
                    }
                );
                if (!cancelled) {
                    setTrustedDomainOptions(Array.isArray(data) ? data : []);
                }
            } catch (e) {
                if (!cancelled && handleEmbedAuthRedirect(e)) {
                    return;
                }
                if (!cancelled) {
                    setTrustedDomainOptions([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const effectiveMissingTrustedDomains = useMemo(() => {
        if (!Array.isArray(missingTrustedDomains) || missingTrustedDomains.length === 0) {
            return [];
        }
        if (!Array.isArray(trustedDomainOptions) || trustedDomainOptions.length === 0) {
            return missingTrustedDomains;
        }
        const availableLower = new Set(
            trustedDomainOptions
                .map((o) => (o && typeof o.domain === "string" ? o.domain.toLowerCase() : null))
                .filter(Boolean)
        );
        return missingTrustedDomains.filter(
            (h) => !availableLower.has(String(h).toLowerCase())
        );
    }, [missingTrustedDomains, trustedDomainOptions]);

    const trustedDomainsWarning = useMemo(() => {
        if (loading) {
            return null;
        }
        if (trustedDomainOptions === null) {
            return null;
        }
        const selectedIds = (trustedDomainIds || []).map(Number);
        const issues = [];
        if (effectiveMissingTrustedDomains.length > 0) {
            issues.push(
                "Trusted domain(s) linked to this form were deleted: " +
                    effectiveMissingTrustedDomains.join(", ") +
                    ". Re-add them under Trusted domains or via the inline button to restore the link."
            );
        }
        const inactiveById = new Map();
        for (const opt of trustedDomainOptions) {
            if (opt && !opt.is_active) {
                inactiveById.set(Number(opt.id), opt.domain);
            }
        }
        const inactiveSelected = selectedIds
            .filter((id) => inactiveById.has(id))
            .map((id) => inactiveById.get(id));
        if (inactiveSelected.length > 0) {
            issues.push(
                "Some selected trusted domains are inactive: " +
                    inactiveSelected.join(", ") +
                    ". The widget won’t load on those sites until they are re-enabled."
            );
        }
        if (
            !isNew &&
            selectedIds.length === 0 &&
            effectiveMissingTrustedDomains.length === 0
        ) {
            issues.push(
                "No trusted domains are linked to this form. The widget can’t load anywhere until you pick at least one."
            );
        }
        return issues.length > 0 ? issues.join(" ") : null;
    }, [
        isNew,
        loading,
        trustedDomainOptions,
        trustedDomainIds,
        effectiveMissingTrustedDomains,
    ]);

    const wizardSteps = useMemo(
        () =>
            EMBED_FORM_WIZARD_STEPS.map((step, idx) => ({
                ...step,
                warning: idx === 1 ? trustedDomainsWarning : null,
            })),
        [trustedDomainsWarning]
    );

    const afterSubmitWizardStep = useMemo(
        () => embedWizardStepIndex(EMBED_FORM_WIZARD_AFTER_STEP_KEY),
        []
    );

    const autoAssignWizardStep = useMemo(
        () => embedWizardStepIndex(EMBED_FORM_WIZARD_AUTO_ASSIGN_STEP_KEY),
        []
    );

    const designWizardStep = useMemo(
        () => embedWizardStepIndex(EMBED_FORM_WIZARD_DESIGN_STEP_KEY),
        []
    );

    useEffect(() => {
        if (wizardStep !== designWizardStep) {
            setDesignPreviewView("form");
        }
    }, [wizardStep, designWizardStep]);

    useEffect(() => {
        if (wizardStep !== afterSubmitWizardStep) {
            setAfterSubmitPreviewModalOpen(false);
        }
    }, [wizardStep, afterSubmitWizardStep]);

    /** Keep live preview fully visible when wizard steps change (tall custom-fields → shorter steps). */
    useEffect(() => {
        const sticky = document.querySelector(
            ".embed-form-editor-preview-sticky"
        );
        if (!sticky) {
            return;
        }
        requestAnimationFrame(() => {
            sticky.scrollIntoView({ block: "nearest", behavior: "smooth" });
        });
    }, [wizardStep]);

    useEffect(() => {
        if (wizardStep !== wizardLastIndex) {
            setLastStepSaveEnabled(false);
            return;
        }
        setLastStepSaveEnabled(false);
        const t = window.setTimeout(() => setLastStepSaveEnabled(true), 450);
        return () => clearTimeout(t);
    }, [wizardStep, wizardLastIndex]);

    useEffect(() => {
        dispatch(fetchDepartments());
        dispatch(fetchUsers());
    }, [dispatch]);

    useEffect(() => {
        setWizardStep(0);
        setWizardError(null);
        if (id) {
            setCreatedEmbedInfo(null);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            return;
        }
        setPersistBaselineJson(newEmbedFormBaselineSnapshotJson());
    }, [id]);

    useEffect(() => {
        if (!id) {
            return;
        }
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(
                    `${baseURL}ticket-embed-form/${id}`,
                    { headers: authHeaders() }
                );
                const loadedCustomFields =
                    Array.isArray(data.custom_fields) &&
                    data.custom_fields.length
                        ? data.custom_fields.map((f) => customFieldFromApi(f))
                        : [];
                setPersistBaselineJson(
                    JSON.stringify(
                        buildEmbedFormPersistSnapshot({
                            name: data.name || "",
                            title: data.title || "",
                            subtitle: data.subtitle || "",
                            titleColor: data.title_color ?? "",
                            subtitleColor: data.subtitle_color ?? "",
                            isActive: data.is_active !== false,
                            trustedDomainIds: (data.trusted_domain_ids || []).map(
                                Number
                            ),
                            departmentId: String(data.department_id ?? ""),
                            backgroundColor:
                                data.background_color || "#ffffff",
                            textColor: data.text_color || "#212529",
                            fieldBackgroundColor:
                                data.field_background_color ?? "",
                            buttonText: data.button_text || "Submit",
                            buttonColor: data.button_color || "",
                            buttonTextColor:
                                data.button_text_color || "#ffffff",
                            formMaxWidth: data.form_max_width || "",
                            formMinWidth: data.form_min_width || "",
                            formBorderRadius:
                                data.form_border_radius != null &&
                                data.form_border_radius !== ""
                                    ? Number(data.form_border_radius)
                                    : EMBED_FORM_DESIGN_INITIAL.formBorderRadius,
                            customCss: data.custom_css ?? "",
                            labelAlign: normalizeEmbedAlign(data.label_align),
                            titleAlign: normalizeEmbedAlign(
                                data.title_align ?? data.subtitle_align
                            ),
                            subtitleAlign: normalizeEmbedAlign(
                                data.subtitle_align ?? data.title_align
                            ),
                            successMessageColor:
                                data.success_message_color || "#212529",
                            successMessageFontFamily:
                                data.success_message_font_family || "poppins",
                            successMessageFontSize:
                                data.success_message_font_size || "1.25rem",
                            successMessageFontWeight:
                                data.success_message_font_weight || "600",
                            displayMode:
                                data.display_mode === EMBED_DISPLAY_MODE_FLOATING
                                    ? EMBED_DISPLAY_MODE_FLOATING
                                    : EMBED_DISPLAY_MODE_INLINE,
                            launcherPosition:
                                data.launcher_position || "bottom-right",
                            launcherColor: data.launcher_color || "",
                            successMessage:
                                data.success_message ||
                                EMBED_FORM_DEFAULT_SUCCESS_MESSAGE,
                            successDisclaimer:
                                data.success_disclaimer != null &&
                                String(data.success_disclaimer).trim() !== ""
                                    ? data.success_disclaimer
                                    : EMBED_DEFAULT_SUCCESS_DISCLAIMER,
                            supportEmail: data.support_email || "",
                            notifyEmailsText: (
                                data.notification_emails || []
                            ).join("\n"),
                            autoAssignUserIds: (
                                data.auto_assign_user_ids || []
                            ).map(Number).filter((id) => id > 0),
                            customFields: loadedCustomFields,
                        })
                    )
                );
                setName(data.name || "");
                setTitle(data.title || "");
                setSubtitle(data.subtitle || "");
                setTitleColor(data.title_color ?? "");
                setSubtitleColor(data.subtitle_color ?? "");
                setIsActive(data.is_active !== false);
                setDisplayMode(
                    data.display_mode === EMBED_DISPLAY_MODE_FLOATING
                        ? EMBED_DISPLAY_MODE_FLOATING
                        : EMBED_DISPLAY_MODE_INLINE
                );
                setLauncherPosition(data.launcher_position || "bottom-right");
                setLauncherColor(data.launcher_color || "");
                setTrustedDomainIds(
                    (data.trusted_domain_ids || []).map(Number)
                );
                setMissingTrustedDomains(
                    Array.isArray(data.missing_trusted_domains)
                        ? data.missing_trusted_domains
                        : []
                );
                setDepartmentId(String(data.department_id ?? ""));
                setBackgroundColor(data.background_color || "#ffffff");
                setTextColor(data.text_color || "#212529");
                setFieldBackgroundColor(data.field_background_color ?? "");
                setButtonText(data.button_text || "Submit");
                setButtonColor(data.button_color || "");
                setButtonTextColor(data.button_text_color || "#ffffff");
                setFormMaxWidth(data.form_max_width || "");
                setFormMinWidth(data.form_min_width || "");
                setFormBorderRadius(
                    data.form_border_radius != null &&
                        data.form_border_radius !== ""
                        ? Number(data.form_border_radius)
                        : EMBED_FORM_DESIGN_INITIAL.formBorderRadius
                );
                setCustomCss(data.custom_css ?? "");
                const loadedTitleAlign = normalizeEmbedAlign(
                    data.title_align ?? data.subtitle_align
                );
                const loadedSubtitleAlign = normalizeEmbedAlign(
                    data.subtitle_align ?? data.title_align
                );
                const loadedLabelAlign = normalizeEmbedAlign(data.label_align);
                setLabelAlign(loadedLabelAlign);
                setTitleAlign(loadedTitleAlign);
                setSubtitleAlign(loadedSubtitleAlign);
                setSuccessMessageColor(
                    data.success_message_color || "#212529"
                );
                setSuccessMessageFontFamily(
                    data.success_message_font_family || "poppins"
                );
                setSuccessMessageFontSize(
                    data.success_message_font_size || "1.25rem"
                );
                setSuccessMessageFontWeight(
                    data.success_message_font_weight || "600"
                );
                setAdvancedAccordionKey(
                    (data.custom_css ?? "").trim() !== "" ||
                        loadedTitleAlign !== "left" ||
                        loadedSubtitleAlign !== "left" ||
                        loadedLabelAlign !== "left"
                        ? "advanced"
                        : null
                );
                setSuccessMessage(
                    data.success_message || EMBED_FORM_DEFAULT_SUCCESS_MESSAGE
                );
                setSuccessDisclaimer(
                    data.success_disclaimer != null &&
                        String(data.success_disclaimer).trim() !== ""
                        ? data.success_disclaimer
                        : EMBED_DEFAULT_SUCCESS_DISCLAIMER
                );
                setSupportEmail(data.support_email || "");
                setNotifyEmailsText(
                    (data.notification_emails || []).join("\n")
                );
                setAutoAssignUserIds(
                    (data.auto_assign_user_ids || [])
                        .map(Number)
                        .filter((id) => id > 0)
                );
                setCustomFields(loadedCustomFields);
            } catch (e) {
                if (handleEmbedAuthRedirect(e)) {
                    return;
                }
                console.error("Failed to load embed form:", e);
                const apiMessage = e.response?.data?.message;
                alertDialog({
                    title: "Could not load form",
                    message:
                        apiMessage ||
                        "The form could not be loaded. You will be returned to the list.",
                });
                skipNavigationBlockRef.current = true;
                history.push("/embed-ticket-forms");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, history]);

    const autoAssignUserOptions = useMemo(
        () =>
            workspaceUsers
                .filter(
                    (u) =>
                        u.workspace_active !== false &&
                        u.workspace_active !== 0 &&
                        u.accepted !== false &&
                        u.accepted !== 0
                )
                .map((u) => ({
                    value: u.id,
                    label: ucFirst(u.name),
                    image: u.profile_image,
                    description: u.email,
                })),
        [workspaceUsers]
    );

    const invalidAutoAssignUserIds = useMemo(
        () =>
            autoAssignUserIds.filter(
                (id) =>
                    !autoAssignUserOptions.some((option) => option.value === id)
            ),
        [autoAssignUserIds, autoAssignUserOptions]
    );

    const autoAssignSelectValue = useMemo(
        () =>
            autoAssignUserIds.map((id) => {
                const match = autoAssignUserOptions.find(
                    (option) => option.value === id
                );
                return (
                    match || {
                        value: id,
                        label: `User #${id} (no longer available)`,
                        isInvalid: true,
                    }
                );
            }),
        [autoAssignUserIds, autoAssignUserOptions]
    );

    const deptSelectOptions = useMemo(
        () =>
            (departments || []).map((d) => ({
                value: d.id,
                label: ucFirst(d.name),
                color: d.color,
            })),
        [departments]
    );

    const selectedDepartment = useMemo(
        () =>
            deptSelectOptions.find(
                (o) => String(o.value) === String(departmentId)
            ) ?? null,
        [deptSelectOptions, departmentId]
    );

    const previewCfgLive = useMemo(
        () => ({
            title: title.trim() || null,
            subtitle: subtitle.trim() || null,
            background_color: backgroundColor || "#ffffff",
            text_color: textColor || "#212529",
            field_background_color: fieldBackgroundColor.trim()
                ? fieldBackgroundColor.trim()
                : null,
            title_color: title.trim() && titleColor.trim()
                ? titleColor.trim()
                : null,
            subtitle_color: subtitle.trim() && subtitleColor.trim()
                ? subtitleColor.trim()
                : null,
            button_text: buttonText || "Submit",
            button_color: buttonColor || null,
            button_text_color: buttonTextColor || null,
            form_max_width: formMaxWidth,
            form_min_width: formMinWidth,
            form_border_radius: formBorderRadius,
            custom_css: customCss,
            label_align: labelAlign,
            title_align: titleAlign,
            subtitle_align: subtitleAlign,
            success_message: successMessage || EMBED_FORM_DEFAULT_SUCCESS_MESSAGE,
            success_disclaimer: successDisclaimer.trim() || null,
            support_email: supportEmail.trim() || null,
            success_message_color: successMessageColor,
            success_message_font_family: successMessageFontFamily,
            success_message_font_size: successMessageFontSize,
            success_message_font_weight: successMessageFontWeight,
            success_checklist_loop: false,
            display_mode: displayMode,
            ...(displayMode === EMBED_DISPLAY_MODE_FLOATING
                ? {
                      launcher_position: launcherPosition,
                      launcher_color: launcherColor || null,
                  }
                : {}),
            custom_fields: customFields.map((f) => ({
                ...f,
                options:
                    embedFieldTypeUsesOptions(f.type)
                        ? normalizedOptionsForSave(f.options || [])
                        : f.options,
            })),
        }),
        [
            title,
            subtitle,
            titleColor,
            subtitleColor,
            backgroundColor,
            textColor,
            fieldBackgroundColor,
            buttonText,
            buttonColor,
            buttonTextColor,
            formMaxWidth,
            formMinWidth,
            formBorderRadius,
            customCss,
            labelAlign,
            titleAlign,
            subtitleAlign,
            successMessage,
            successDisclaimer,
            supportEmail,
            successMessageColor,
            successMessageFontFamily,
            successMessageFontSize,
            successMessageFontWeight,
            displayMode,
            launcherPosition,
            launcherColor,
            customFields,
        ]
    );

    const previewCfg = previewCfgLive;

    /**
     * Native `<input type="color">` fires `onChange` on every pixel while the user drags
     * inside the picker dialog. Throttle the React state setter so the (large) editor
     * only re-renders at ~12fps instead of 60–100fps while dragging. The native dialog
     * keeps drawing live; only React catches up at the throttled rate.
     */
    const throttledSetTitleColor = useThrottledCallback(setTitleColor, 80);
    const throttledSetSubtitleColor = useThrottledCallback(setSubtitleColor, 80);
    const throttledSetBackgroundColor = useThrottledCallback(
        setBackgroundColor,
        80
    );
    const throttledSetTextColor = useThrottledCallback(setTextColor, 80);
    const throttledSetFieldBackgroundColor = useThrottledCallback(
        setFieldBackgroundColor,
        80
    );
    const throttledSetButtonColor = useThrottledCallback(setButtonColor, 80);
    const throttledSetLauncherColor = useThrottledCallback(
        setLauncherColor,
        80
    );
    const throttledSetButtonTextColor = useThrottledCallback(
        setButtonTextColor,
        80
    );
    const throttledSetSuccessMessageColor = useThrottledCallback(
        setSuccessMessageColor,
        80
    );

    const embedMinMaxWidthConflict = useMemo(() => {
        const minPx = parseNumericPxFromEmbedWidth(formMinWidth);
        const maxPx = parseNumericPxFromEmbedWidth(formMaxWidth);
        if (minPx == null || maxPx == null) {
            return false;
        }
        return minPx > maxPx;
    }, [formMinWidth, formMaxWidth]);

    const wizardFieldBag = useMemo(
        () => ({
            name,
            departmentId,
            trustedDomainIds,
            successMessage,
            supportEmail,
            customFields,
        }),
        [name, departmentId, trustedDomainIds, successMessage, supportEmail, customFields]
    );

    const persistSnapshotJson = useMemo(
        () =>
            JSON.stringify(
                buildEmbedFormPersistSnapshot({
                    name,
                    title,
                    subtitle,
                    titleColor,
                    subtitleColor,
                    isActive,
                    trustedDomainIds,
                    departmentId,
                    backgroundColor,
                    textColor,
                    fieldBackgroundColor,
                    buttonText,
                    buttonColor,
                    buttonTextColor,
                    formMaxWidth,
                    formMinWidth,
                    formBorderRadius,
                    customCss,
                    labelAlign,
                    titleAlign,
                    subtitleAlign,
                    successMessageColor,
                    successMessageFontFamily,
                    successMessageFontSize,
                    successMessageFontWeight,
                    displayMode,
                    launcherPosition,
                    launcherColor,
                    successMessage,
                    successDisclaimer,
                    supportEmail,
                    notifyEmailsText,
                    autoAssignUserIds,
                    customFields,
                })
            ),
        [
            name,
            title,
            subtitle,
            titleColor,
            subtitleColor,
            isActive,
            displayMode,
            launcherPosition,
            launcherColor,
            trustedDomainIds,
            departmentId,
            backgroundColor,
            textColor,
            fieldBackgroundColor,
            buttonText,
            buttonColor,
            buttonTextColor,
            formMaxWidth,
            formMinWidth,
            formBorderRadius,
            customCss,
            labelAlign,
            titleAlign,
            subtitleAlign,
            successMessageColor,
            successMessageFontFamily,
            successMessageFontSize,
            successMessageFontWeight,
            successMessage,
            successDisclaimer,
            supportEmail,
            notifyEmailsText,
            autoAssignUserIds,
            customFields,
        ]
    );

    const baselineReady = persistBaselineJson !== null;
    const hasDirty =
        baselineReady && persistSnapshotJson !== persistBaselineJson;

    const ensureAuthOrRedirect = useCallback(() => {
        if (authCheckInFlightRef.current) {
            return;
        }
        authCheckInFlightRef.current = true;
        axios
            .get(`${baseURL}user`, { headers: authHeaders() })
            .catch((e) => {
                handleEmbedAuthRedirect(e);
            })
            .finally(() => {
                authCheckInFlightRef.current = false;
            });
    }, []);

    const navigateToFormsList = useCallback(() => {
        history.push("/embed-ticket-forms");
    }, [history]);

    const cancelLeavePage = useCallback(() => {
        if (confirmingLeaveRef.current) {
            return;
        }
        pendingNavigationRef.current = null;
        leavePromptOpenRef.current = false;
        setLeaveConfirmOpen(false);
    }, []);

    const confirmLeavePage = useCallback(() => {
        const pending = pendingNavigationRef.current;
        if (!pending?.path) {
            cancelLeavePage();
            return;
        }

        const { path, state, action } = pending;
        confirmingLeaveRef.current = true;
        pendingNavigationRef.current = null;
        leavePromptOpenRef.current = false;

        const unblock = navigationUnblockRef.current;
        if (unblock) {
            unblock();
            navigationUnblockRef.current = null;
        }

        if (action === "POP") {
            history.goBack();
        } else if (action === "REPLACE") {
            history.replace(path, state);
        } else {
            history.push(path, state);
        }

        setLeaveConfirmOpen(false);
        confirmingLeaveRef.current = false;
    }, [history, cancelLeavePage]);

    useEffect(() => {
        if (!hasDirty || saving) {
            return undefined;
        }
        const onBeforeUnload = (event) => {
            // If auth is missing/expired, don't trap the user with a native
            // "Changes you made may not be saved" prompt. We want auth handling
            // (redirect to login) to be forceful.
            if (!Cookies.get("access_token")) {
                return;
            }
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [hasDirty, saving]);

    useEffect(() => {
        if (!hasDirty) {
            skipNavigationBlockRef.current = false;
        }
        if (!hasDirty || saving) {
            pendingNavigationRef.current = null;
            leavePromptOpenRef.current = false;
            setLeaveConfirmOpen(false);
        }
    }, [hasDirty, saving]);

    useEffect(() => {
        if (!hasDirty || saving) {
            return undefined;
        }
        const unblock = history.block((location, action) => {
            if (skipNavigationBlockRef.current) {
                return;
            }

            const path = blockedNavigationPath(location);
            if (!path) {
                return false;
            }

            pendingNavigationRef.current = { path, state: location.state, action };

            if (!leavePromptOpenRef.current) {
                leavePromptOpenRef.current = true;
                setLeaveConfirmOpen(true);
                // If the session expired, don't let the unsaved-changes prompt "win".
                // Trigger an auth check and redirect to `/login` on 401.
                ensureAuthOrRedirect();
            }

            return false;
        });
        navigationUnblockRef.current = unblock;
        return () => {
            navigationUnblockRef.current = null;
            unblock();
        };
    }, [hasDirty, saving, history, ensureAuthOrRedirect]);

    const showSaveButton = isNew
        ? wizardStep === wizardLastIndex && createdEmbedInfo == null
        : hasDirty;

    const tryGoToWizardStep = (targetStep) => {
        if (targetStep === wizardStep) {
            return;
        }
        if (targetStep < wizardStep) {
            setWizardError(null);
            setWizardStep(targetStep);
            return;
        }
        for (let s = wizardStep; s < targetStep; s++) {
            if (!wizardStepSatisfied(s, wizardFieldBag)) {
                setWizardError(wizardStepErrorMessage(s, wizardFieldBag));
                return;
            }
        }
        setWizardError(null);
        setWizardStep(targetStep);
    };

    const wizardStepNext = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (!wizardStepSatisfied(wizardStep, wizardFieldBag)) {
            setWizardError(wizardStepErrorMessage(wizardStep, wizardFieldBag));
            return;
        }
        setWizardError(null);
        setWizardStep((x) => Math.min(wizardLastIndex, x + 1));
    };

    const confirmResetEmbedDesignToDefaults = () => {
        const resettingSuccess =
            designWizardStep >= 0 &&
            wizardStep === designWizardStep &&
            designPreviewView === "success";
        const message = resettingSuccess
            ? "Reset success screen colors and fonts to defaults?"
            : "Reset form colors and layout to defaults?";
        confirmDialog({
            title: "Reset to defaults?",
            message,
            confirmLabel: "Reset",
            cancelLabel: "Cancel",
            onConfirm: () => {
                if (resettingSuccess) {
                    setSuccessMessageColor(
                        EMBED_SUCCESS_DESIGN_INITIAL.successMessageColor
                    );
                    setSuccessMessageFontFamily(
                        EMBED_SUCCESS_DESIGN_INITIAL.successMessageFontFamily
                    );
                    setSuccessMessageFontSize(
                        EMBED_SUCCESS_DESIGN_INITIAL.successMessageFontSize
                    );
                    setSuccessMessageFontWeight(
                        EMBED_SUCCESS_DESIGN_INITIAL.successMessageFontWeight
                    );
                } else {
                    setBackgroundColor(EMBED_FORM_DESIGN_INITIAL.backgroundColor);
                    setTextColor(EMBED_FORM_DESIGN_INITIAL.textColor);
                    setFieldBackgroundColor(
                        EMBED_FORM_DESIGN_INITIAL.fieldBackgroundColor
                    );
                    setButtonText(EMBED_FORM_DESIGN_INITIAL.buttonText);
                    setButtonColor(EMBED_FORM_DESIGN_INITIAL.buttonColor);
                    setButtonTextColor(EMBED_FORM_DESIGN_INITIAL.buttonTextColor);
                    setFormMaxWidth(EMBED_FORM_DESIGN_INITIAL.formMaxWidth);
                    setFormMinWidth(EMBED_FORM_DESIGN_INITIAL.formMinWidth);
                    setFormBorderRadius(
                        EMBED_FORM_DESIGN_INITIAL.formBorderRadius
                    );
                    setCustomCss(EMBED_FORM_DESIGN_INITIAL.customCss);
                    setLabelAlign(EMBED_FORM_DESIGN_INITIAL.labelAlign);
                    setTitleAlign(EMBED_FORM_DESIGN_INITIAL.titleAlign);
                    setSubtitleAlign(EMBED_FORM_DESIGN_INITIAL.subtitleAlign);
                    setAdvancedAccordionKey(null);
                }
            },
        });
    };

    const addField = () =>
        setCustomFields((prev) => [...prev, emptyField()]);

    const updateField = (idx, patch) => {
        setCustomFields((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };

    const removeField = (idx) => {
        setCustomFields((prev) => prev.filter((_, i) => i !== idx));
    };

    const moveCustomField = (fromIndex, toIndex) => {
        setCustomFields((prev) => {
            if (
                fromIndex === toIndex ||
                fromIndex < 0 ||
                toIndex < 0 ||
                fromIndex >= prev.length ||
                toIndex >= prev.length
            ) {
                return prev;
            }
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    };

    const handleFieldDragStart = (ev, index) => {
        draggedFieldIndexRef.current = index;
        ev.dataTransfer.effectAllowed = "move";
        ev.dataTransfer.setData("text/plain", String(index));
        if (ev.currentTarget instanceof HTMLElement) {
            ev.currentTarget.closest("tr")?.classList.add(
                "embed-form-custom-fields-row--dragging"
            );
        }
    };

    const handleFieldDragEnd = (ev) => {
        draggedFieldIndexRef.current = null;
        setDragOverFieldIndex(null);
        if (ev.currentTarget instanceof HTMLElement) {
            ev.currentTarget.closest("tr")?.classList.remove(
                "embed-form-custom-fields-row--dragging"
            );
        }
    };

    const handleFieldDragOver = (ev, index) => {
        ev.preventDefault();
        ev.dataTransfer.dropEffect = "move";
        if (dragOverFieldIndex !== index) {
            setDragOverFieldIndex(index);
        }
    };

    const handleFieldDragLeave = (ev, index) => {
        const related = ev.relatedTarget;
        if (
            related instanceof Node &&
            ev.currentTarget instanceof Node &&
            ev.currentTarget.contains(related)
        ) {
            return;
        }
        if (dragOverFieldIndex === index) {
            setDragOverFieldIndex(null);
        }
    };

    const handleFieldDrop = (ev, index) => {
        ev.preventDefault();
        const fromIndex = draggedFieldIndexRef.current;
        draggedFieldIndexRef.current = null;
        setDragOverFieldIndex(null);
        if (fromIndex == null) {
            return;
        }
        moveCustomField(fromIndex, index);
    };

    const submit = async (ev) => {
        ev.preventDefault();
        for (let s = 0; s <= wizardLastIndex; s++) {
            if (!wizardStepSatisfied(s, wizardFieldBag)) {
                setWizardStep(s);
                setWizardError(wizardStepErrorMessage(s, wizardFieldBag));
                return;
            }
        }
        const trusted_domain_ids = [...trustedDomainIds].map(Number);
        const notification_emails = notifyEmailsText
            .split(/[\n,]+/)
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

        const payload = {
            name,
            title: title || null,
            subtitle: subtitle || null,
            is_active: isActive,
            trusted_domain_ids,
            department_id: Number(departmentId),
            background_color: backgroundColor,
            text_color: textColor || null,
            field_background_color: fieldBackgroundColor.trim() || null,
            title_color:
                title.trim() && titleColor.trim()
                    ? titleColor.trim()
                    : null,
            subtitle_color:
                subtitle.trim() && subtitleColor.trim()
                    ? subtitleColor.trim()
                    : null,
            button_text: buttonText,
            button_color: buttonColor || null,
            button_text_color: buttonTextColor || null,
            form_max_width: formMaxWidth || null,
            form_min_width: formMinWidth || null,
            form_border_radius: Math.min(
                64,
                Math.max(0, Number(formBorderRadius) || 0)
            ),
            custom_css: customCss.trim() || null,
            label_align: labelAlign,
            title_align: titleAlign,
            subtitle_align: subtitleAlign,
            success_message: successMessage,
            success_disclaimer: successDisclaimer.trim() || null,
            support_email: supportEmail.trim() || null,
            success_message_color: successMessageColor.trim() || null,
            success_message_font_family:
                successMessageFontFamily.trim() || null,
            success_message_font_size: successMessageFontSize.trim() || null,
            success_message_font_weight:
                successMessageFontWeight.trim() || null,
            success_checklist_loop: false,
            display_mode: displayMode,
            ...(displayMode === EMBED_DISPLAY_MODE_FLOATING
                ? {
                      launcher_position: launcherPosition,
                      launcher_color: launcherColor || null,
                  }
                : {}),
            notification_emails,
            auto_assign_user_ids: autoAssignUserIds
                .map(Number)
                .filter((id) => id > 0),
            custom_fields: customFields.map((f) => {
                const row = {
                    label: String(f.label ?? "").trim(),
                    name: f.name || undefined,
                    placeholder: f.placeholder || "",
                    type: f.type,
                    required: f.required,
                    options: embedFieldTypeUsesOptions(f.type)
                        ? normalizedOptionsForSave(f.options)
                        : [],
                };
                const layout = customFieldOptionsLayoutForSave(f);
                if (layout !== undefined) {
                    row.options_layout = layout;
                }
                return row;
            }),
        };

        setSaving(true);
        try {
            if (isNew) {
                const { data } = await axios.post(
                    `${baseURL}ticket-embed-form`,
                    payload,
                    { headers: authHeaders() }
                );
                setPersistBaselineJson(persistSnapshotJson);
                setCreatedEmbedInfo({
                    id: data.id,
                    uuid: data.uuid,
                    name: data.name ?? name,
                });
            } else {
                await axios.post(
                    `${baseURL}ticket-embed-form-update/${id}`,
                    payload,
                    { headers: authHeaders() }
                );
                setPersistBaselineJson(persistSnapshotJson);
                navigateToFormsList();
            }
        } catch (e) {
            if (handleEmbedAuthRedirect(e)) {
                return;
            }
            const { message, step } = parseEmbedFormSaveValidationError(e);
            if (step !== null) {
                setWizardStep(step);
                setWizardError(message);
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
                alertDialog({
                    title: "Could not save",
                    message,
                });
            }
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <MainContainer>
                <Container className="pt-4">Loading…</Container>
            </MainContainer>
        );
    }

    const bgPicker = colorPickerValue(backgroundColor, "#ffffff");
    const btnPicker = colorPickerValue(
        buttonColor || EMBED_DEFAULT_BUTTON_COLOR,
        EMBED_DEFAULT_BUTTON_COLOR
    );
    const launcherColorPicker = colorPickerValue(
        launcherColor || buttonColor || EMBED_DEFAULT_BUTTON_COLOR,
        EMBED_DEFAULT_BUTTON_COLOR
    );
    const textPicker = colorPickerValue(textColor, "#212529");
    const btnTextPicker = colorPickerValue(
        buttonTextColor || "#ffffff",
        "#ffffff"
    );
    const fieldBgAuto = embedFieldChrome(
        textColor || "#212529",
        backgroundColor || "#ffffff"
    ).inputBg;
    const fieldBgPicker = colorPickerValue(
        fieldBackgroundColor || fieldBgAuto,
        fieldBgAuto
    );
    const titleColorPicker = colorPickerValue(
        titleColor || textColor,
        textColor || "#212529"
    );
    const subtitleColorPicker = colorPickerValue(
        subtitleColor || textColor,
        textColor || "#212529"
    );
    const successMessagePicker = colorPickerValue(
        successMessageColor,
        "#212529"
    );

    const closeCreatedEmbedModalToList = () => {
        setCreatedEmbedInfo(null);
        navigateToFormsList();
    };

    const goToEditCreatedForm = () => {
        if (!createdEmbedInfo?.id) {
            return;
        }
        const editId = createdEmbedInfo.id;
        setCreatedEmbedInfo(null);
        history.replace(`/embed-ticket-forms/${editId}`);
    };

    return (
        <MainContainer>
            <Container fluid className="pb-5 embed-form-editor-page">
                <Row className="pt-4 pb-2">
                    <Col className="table-page-header">
                        <div className="flex-grow-1 text-align-left">
                            <h5 className="m-0">
                                {isNew ? "New embed form" : "Edit embed form"}
                            </h5>
                            <p className="text-muted small mb-0">
                                Step through sections — preview stays in sync on
                                the right.
                            </p>
                        </div>
                    </Col>
                </Row>
                <Row className="g-4 align-items-start">
                        <Col xl={7} lg={12} className="embed-form-editor-settings-col">
                            <Form
                                onSubmit={submit}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === "Enter" &&
                                        e.target.tagName !== "TEXTAREA" &&
                                        e.target.tagName !== "BUTTON"
                                    ) {
                                        e.preventDefault();
                                    }
                                }}
                            >
                            <EmbedFormWizardStepper
                                steps={wizardSteps}
                                activeIndex={wizardStep}
                                onStepClick={tryGoToWizardStep}
                            />
                            {wizardError ? (
                                <Alert
                                    variant="warning"
                                    className="embed-form-editor-wizard-alert py-2 small mb-3"
                                    dismissible
                                    onClose={() => setWizardError(null)}
                                >
                                    {wizardError}
                                </Alert>
                            ) : null}
                            <div className="embed-form-editor-step-body">
                            {wizardStep === 0 ? (
                            <Row className="g-3">
                                <Col xs={12}>
                                    <h2 className="embed-form-section-title">
                                        General
                                    </h2>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            <RequiredFieldLabel>
                                                Form name
                                            </RequiredFieldLabel>
                                        </Form.Label>
                                        <Form.Control
                                            value={name}
                                            onChange={(e) =>
                                                setName(e.target.value)
                                            }
                                            placeholder="Contact Form"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            <RequiredFieldLabel>
                                                <EmbedDepartmentLabelHint id="embed-form-editor-dept" />
                                            </RequiredFieldLabel>
                                        </Form.Label>
                                        <Select
                                            options={deptSelectOptions}
                                            value={selectedDepartment}
                                            onChange={(opt) =>
                                                setDepartmentId(
                                                    opt ? String(opt.value) : ""
                                                )
                                            }
                                            components={{
                                                Option: colorOption,
                                                SingleValue: colorValueSingle,
                                            }}
                                            classNamePrefix="select-component"
                                            placeholder="Select…"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col
                                    md={6}
                                    className="d-flex align-items-end"
                                >
                                    <Form.Check
                                        type="switch"
                                        id="active-switch"
                                        label="Active"
                                        checked={isActive}
                                        onChange={(e) =>
                                            setIsActive(e.target.checked)
                                        }
                                    />
                                </Col>
                                <Col xs={12}>
                                    <hr
                                        className="embed-form-editor-design-sep"
                                        role="presentation"
                                    />
                                </Col>
                                <Col xs={12}>
                                    <h3 className="h6 fw-semibold mb-2">
                                        How it appears on your site
                                    </h3>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Display</Form.Label>
                                        <Form.Select
                                            value={displayMode}
                                            onChange={(e) =>
                                                setDisplayMode(e.target.value)
                                            }
                                        >
                                            <option
                                                value={EMBED_DISPLAY_MODE_INLINE}
                                            >
                                                Inline — takes space on the page
                                            </option>
                                            <option
                                                value={
                                                    EMBED_DISPLAY_MODE_FLOATING
                                                }
                                            >
                                                Floating button — opens in a
                                                corner
                                            </option>
                                        </Form.Select>
                                        <Form.Text>
                                            Inline uses a mount div where you
                                            place the script. Floating adds a
                                            round button fixed to the browser
                                            corner (like chat).
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                {displayMode === EMBED_DISPLAY_MODE_FLOATING ? (
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                Button position
                                            </Form.Label>
                                            <Form.Select
                                                value={launcherPosition}
                                                onChange={(e) =>
                                                    setLauncherPosition(
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                {EMBED_LAUNCHER_POSITION_OPTIONS.map(
                                                    (o) => (
                                                        <option
                                                            key={o.value}
                                                            value={o.value}
                                                        >
                                                            {o.label}
                                                        </option>
                                                    )
                                                )}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                ) : null}
                                {displayMode === EMBED_DISPLAY_MODE_FLOATING ? (
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                Button color
                                            </Form.Label>
                                            <div className="embed-form-editor-color-row">
                                                <Form.Control
                                                    type="text"
                                                    value={launcherColor}
                                                    onChange={(e) =>
                                                        setLauncherColor(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Matches button fill if empty"
                                                    className="embed-form-editor-color-row__text"
                                                />
                                                <input
                                                    type="color"
                                                    className="embed-form-editor-color-row__picker"
                                                    value={launcherColorPicker}
                                                    onChange={(e) =>
                                                        throttledSetLauncherColor(
                                                            e.target.value
                                                        )
                                                    }
                                                    title="Pick floating button color"
                                                    aria-label="Pick floating button color"
                                                />
                                            </div>
                                            <Form.Text>
                                                Background of the floating corner
                                                button. Leave empty to reuse the
                                                form’s button fill.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                ) : null}
                                <Col xs={12}>
                                    <hr
                                        className="embed-form-editor-design-sep"
                                        role="presentation"
                                    />
                                </Col>
                                <Col xs={12}>
                                    <h3 className="h6 fw-semibold mb-2">
                                        Heading
                                    </h3>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Title</Form.Label>
                                        <Form.Control
                                            value={title}
                                            onChange={(e) =>
                                                setTitle(e.target.value)
                                            }
                                            placeholder="Contact us"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Subtitle</Form.Label>
                                        <Form.Control
                                            value={subtitle}
                                            onChange={(e) =>
                                                setSubtitle(e.target.value)
                                            }
                                            placeholder="We usually reply within one business day"
                                        />
                                    </Form.Group>
                                </Col>
                                {title.trim() ? (
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                Title color
                                            </Form.Label>
                                            <div className="embed-form-editor-color-row">
                                                <Form.Control
                                                    type="text"
                                                    value={titleColor}
                                                    onChange={(e) =>
                                                        setTitleColor(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Same as label text"
                                                    className="embed-form-editor-color-row__text"
                                                />
                                                <input
                                                    type="color"
                                                    className="embed-form-editor-color-row__picker"
                                                    value={titleColorPicker}
                                                    onChange={(e) =>
                                                        throttledSetTitleColor(
                                                            e.target.value
                                                        )
                                                    }
                                                    title="Pick title color"
                                                    aria-label="Pick title color"
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                ) : null}
                                {subtitle.trim() ? (
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>
                                                Subtitle color
                                            </Form.Label>
                                            <div className="embed-form-editor-color-row">
                                                <Form.Control
                                                    type="text"
                                                    value={subtitleColor}
                                                    onChange={(e) =>
                                                        setSubtitleColor(
                                                            e.target.value
                                                        )
                                                    }
                                                    placeholder="Muted label text"
                                                    className="embed-form-editor-color-row__text"
                                                />
                                                <input
                                                    type="color"
                                                    className="embed-form-editor-color-row__picker"
                                                    value={subtitleColorPicker}
                                                    onChange={(e) =>
                                                        throttledSetSubtitleColor(
                                                            e.target.value
                                                        )
                                                    }
                                                    title="Pick subtitle color"
                                                    aria-label="Pick subtitle color"
                                                />
                                            </div>
                                            <Form.Text>
                                                Default subtitle uses label
                                                color at 75% opacity; a custom
                                                color applies at full strength.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                ) : null}
                            </Row>
                            ) : wizardStep === 1 ? (
                            <Row className="g-3">
                                <Col xs={12}>
                                    <h2 className="embed-form-section-title">
                                        Trusted domains
                                    </h2>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label>
                                            Allowed hostnames
                                        </Form.Label>
                                        <EmbedTrustedDomainPicker
                                            value={trustedDomainIds}
                                            onChange={setTrustedDomainIds}
                                            onOptionsLoaded={
                                                setTrustedDomainOptions
                                            }
                                        />
                                        <Form.Text className="mb-0 text-start">
                                            Subdomains of selected hosts are
                                            allowed automatically.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                            ) : wizardStep === designWizardStep ? (
                            <Row className="g-3">
                                <Col xs={12}>
                                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                                        <div>
                                            <h2 className="embed-form-section-title">
                                                Design
                                            </h2>
                                            <p className="text-muted small mb-0">
                                                {designPreviewView === "form"
                                                    ? "Colors and layout for the ticket form. Switch the preview to After submit to style the success screen."
                                                    : "Success message text and colors. Use the preview toggle below the live preview."}
                                                {designPreviewView === "form" ? (
                                                    <>
                                                        {" "}
                                                        For min / max width,
                                                        bare numbers use pixels
                                                        (e.g.{" "}
                                                        <span className="text-nowrap">
                                                            480
                                                        </span>{" "}
                                                        → 480px).
                                                    </>
                                                ) : null}
                                            </p>
                                        </div>
                                        <Button
                                            type="button"
                                            variant={EmbedFormBtn.danger}
                                            className="flex-shrink-0"
                                            onClick={
                                                confirmResetEmbedDesignToDefaults
                                            }
                                        >
                                            {designPreviewView === "success"
                                                ? "Reset success styling"
                                                : "Reset form styling"}
                                        </Button>
                                    </div>
                                </Col>
                                {designPreviewView === "success" ? (
                                    <>
                                        <Col xs={12}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Success message
                                                </Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    value={successMessage}
                                                    onChange={(e) =>
                                                        setSuccessMessage(
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                />
                                                <Form.Text>
                                                    Same message as the After
                                                    submit step. Style it here;
                                                    use line breaks for separate
                                                    paragraphs.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Message color
                                                </Form.Label>
                                                <div className="embed-form-editor-color-row">
                                                    <Form.Control
                                                        type="text"
                                                        value={
                                                            successMessageColor
                                                        }
                                                        onChange={(e) =>
                                                            setSuccessMessageColor(
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="#212529"
                                                        className="embed-form-editor-color-row__text"
                                                    />
                                                    <input
                                                        type="color"
                                                        className="embed-form-editor-color-row__picker"
                                                        value={
                                                            successMessagePicker
                                                        }
                                                        onChange={(e) =>
                                                            throttledSetSuccessMessageColor(
                                                                e.target.value
                                                            )
                                                        }
                                                        title="Pick success message color"
                                                        aria-label="Pick success message color"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>Font</Form.Label>
                                                <Form.Select
                                                    value={
                                                        successMessageFontFamily
                                                    }
                                                    onChange={(e) =>
                                                        setSuccessMessageFontFamily(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    {EMBED_SUCCESS_FONT_FAMILY_OPTIONS.map(
                                                        (o) => (
                                                            <option
                                                                key={o.value}
                                                                value={o.value}
                                                            >
                                                                {o.label}
                                                            </option>
                                                        )
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Font size
                                                </Form.Label>
                                                <Form.Select
                                                    value={
                                                        successMessageFontSize
                                                    }
                                                    onChange={(e) =>
                                                        setSuccessMessageFontSize(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    {EMBED_SUCCESS_FONT_SIZE_OPTIONS.map(
                                                        (o) => (
                                                            <option
                                                                key={o.value}
                                                                value={o.value}
                                                            >
                                                                {o.label}
                                                            </option>
                                                        )
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label>
                                                    Font weight
                                                </Form.Label>
                                                <Form.Select
                                                    value={
                                                        successMessageFontWeight
                                                    }
                                                    onChange={(e) =>
                                                        setSuccessMessageFontWeight(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    {EMBED_SUCCESS_FONT_WEIGHT_OPTIONS.map(
                                                        (o) => (
                                                            <option
                                                                key={o.value}
                                                                value={o.value}
                                                            >
                                                                {o.label}
                                                            </option>
                                                        )
                                                    )}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </>
                                ) : (
                                    <>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Background</Form.Label>
                                        <div className="embed-form-editor-color-row">
                                            <Form.Control
                                                type="text"
                                                value={backgroundColor}
                                                onChange={(e) =>
                                                    setBackgroundColor(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="#ffffff"
                                                className="embed-form-editor-color-row__text"
                                            />
                                            <input
                                                type="color"
                                                className="embed-form-editor-color-row__picker"
                                                value={bgPicker}
                                                onChange={(e) =>
                                                    throttledSetBackgroundColor(
                                                        e.target.value
                                                    )
                                                }
                                                title="Pick color"
                                                aria-label="Pick background color"
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Text &amp; labels</Form.Label>
                                        <div className="embed-form-editor-color-row">
                                            <Form.Control
                                                type="text"
                                                value={textColor}
                                                onChange={(e) =>
                                                    setTextColor(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="#212529"
                                                className="embed-form-editor-color-row__text"
                                            />
                                            <input
                                                type="color"
                                                className="embed-form-editor-color-row__picker"
                                                value={textPicker}
                                                onChange={(e) =>
                                                    throttledSetTextColor(
                                                        e.target.value
                                                    )
                                                }
                                                title="Pick text color"
                                                aria-label="Pick text color"
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Fields background
                                        </Form.Label>
                                        <div className="embed-form-editor-color-row">
                                            <Form.Control
                                                type="text"
                                                value={fieldBackgroundColor}
                                                onChange={(e) =>
                                                    setFieldBackgroundColor(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Auto from card"
                                                className="embed-form-editor-color-row__text"
                                            />
                                            <input
                                                type="color"
                                                className="embed-form-editor-color-row__picker"
                                                value={fieldBgPicker}
                                                onChange={(e) =>
                                                    throttledSetFieldBackgroundColor(
                                                        e.target.value
                                                    )
                                                }
                                                title="Pick fields background"
                                                aria-label="Pick fields background"
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Primary / button fill
                                        </Form.Label>
                                        <div className="embed-form-editor-color-row">
                                            <Form.Control
                                                type="text"
                                                value={buttonColor}
                                                onChange={(e) =>
                                                    setButtonColor(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Default blue if empty"
                                                className="embed-form-editor-color-row__text"
                                            />
                                            <input
                                                type="color"
                                                className="embed-form-editor-color-row__picker"
                                                value={btnPicker}
                                                onChange={(e) =>
                                                    throttledSetButtonColor(
                                                        e.target.value
                                                    )
                                                }
                                                title="Pick button color"
                                                aria-label="Pick button color"
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Button label color</Form.Label>
                                        <div className="embed-form-editor-color-row">
                                            <Form.Control
                                                type="text"
                                                value={buttonTextColor}
                                                onChange={(e) =>
                                                    setButtonTextColor(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="#ffffff"
                                                className="embed-form-editor-color-row__text"
                                            />
                                            <input
                                                type="color"
                                                className="embed-form-editor-color-row__picker"
                                                value={btnTextPicker}
                                                onChange={(e) =>
                                                    throttledSetButtonTextColor(
                                                        e.target.value
                                                    )
                                                }
                                                title="Pick button text color"
                                                aria-label="Pick button text color"
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Button label</Form.Label>
                                        <Form.Control
                                            value={buttonText}
                                            onChange={(e) =>
                                                setButtonText(e.target.value)
                                            }
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Form min width</Form.Label>
                                        <Form.Control
                                            value={formMinWidth}
                                            onChange={(e) =>
                                                setFormMinWidth(e.target.value)
                                            }
                                            placeholder="e.g. 320 or 320px"
                                        />
                                        <Form.Text>
                                            Optional floor for the card width.
                                            Leave empty for no minimum.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Form max width</Form.Label>
                                        <Form.Control
                                            value={formMaxWidth}
                                            onChange={(e) =>
                                                setFormMaxWidth(e.target.value)
                                            }
                                            placeholder="480px or 100%"
                                        />
                                        <Form.Text>
                                            Cap the card width. Empty defaults
                                            to full width of the embed slot.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                {embedMinMaxWidthConflict ? (
                                    <Col xs={12}>
                                        <Alert
                                            variant="warning"
                                            className="small py-2 mb-0"
                                        >
                                            Min width is larger than max width,
                                            so those rules work against each
                                            other on the live site. Adjust the
                                            values until min is less than or
                                            equal to max.
                                        </Alert>
                                    </Col>
                                ) : null}
                                <Col xs={12}>
                                    <hr
                                        className="embed-form-editor-design-sep"
                                        role="presentation"
                                    />
                                </Col>
                                <Col xs={12}>
                                    <Accordion
                                        className="embed-form-editor-advanced"
                                        activeKey={advancedAccordionKey}
                                        onSelect={(key) =>
                                            setAdvancedAccordionKey(
                                                key === "advanced"
                                                    ? "advanced"
                                                    : null
                                            )
                                        }
                                    >
                                        <Accordion.Item eventKey="advanced">
                                            <Accordion.Header>
                                                Advanced
                                            </Accordion.Header>
                                            <Accordion.Body className="pt-2">
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="d-flex justify-content-between align-items-center mb-2">
                                                        <span>Border radius</span>
                                                        <span className="text-muted small fw-normal">
                                                            {formBorderRadius}px
                                                        </span>
                                                    </Form.Label>
                                                    <Form.Range
                                                        min={0}
                                                        max={48}
                                                        step={1}
                                                        value={formBorderRadius}
                                                        onChange={(e) =>
                                                            setFormBorderRadius(
                                                                Number(
                                                                    e.target
                                                                        .value
                                                                )
                                                            )
                                                        }
                                                    />
                                                    <Form.Text className="d-block text-center mt-2 mb-0">
                                                        Rounds only the outer
                                                        form card on your site
                                                        and in the preview.
                                                        Field and button corners
                                                        stay at the default size.
                                                    </Form.Text>
                                                </Form.Group>
                                                <hr
                                                    className="embed-form-editor-design-sep"
                                                    role="presentation"
                                                />
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="d-block text-center mb-2">
                                                        Title alignment
                                                    </Form.Label>
                                                    <EmbedAlignButtonGroup
                                                        value={titleAlign}
                                                        onChange={setTitleAlign}
                                                    />
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="d-block text-center mb-2">
                                                        Subtitle alignment
                                                    </Form.Label>
                                                    <EmbedAlignButtonGroup
                                                        value={subtitleAlign}
                                                        onChange={
                                                            setSubtitleAlign
                                                        }
                                                    />
                                                </Form.Group>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="d-block text-center mb-2">
                                                        Label alignment
                                                    </Form.Label>
                                                    <EmbedAlignButtonGroup
                                                        value={labelAlign}
                                                        onChange={setLabelAlign}
                                                    />
                                                    <Form.Text className="d-block text-center mt-4 mb-0">
                                                        Alignment of the small
                                                        label text above each
                                                        field (including radio
                                                        groups). Checkbox rows
                                                        align as a group.
                                                    </Form.Text>
                                                </Form.Group>
                                                <hr
                                                    className="embed-form-editor-design-sep"
                                                    role="presentation"
                                                />
                                                <Form.Group className="mb-0">
                                                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                                        <Form.Label className="mb-0">
                                                            Custom CSS
                                                        </Form.Label>
                                                        <Button
                                                            type="button"
                                                            variant={EmbedFormBtn.secondary}
                                                            size="sm"
                                                            className="py-0 px-2 d-inline-flex align-items-center gap-1"
                                                            onClick={() =>
                                                                setShowEmbedCssHelp(
                                                                    true
                                                                )
                                                            }
                                                            aria-label="Widget CSS class reference"
                                                        >
                                                            <BsInfoCircle aria-hidden />
                                                            Classes
                                                        </Button>
                                                    </div>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={8}
                                                        value={customCss}
                                                        onChange={(e) =>
                                                            setCustomCss(
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder={`.claritick-ticket-embed-card { border-radius: 12px; }`}
                                                        className="font-monospace small"
                                                        spellCheck="false"
                                                    />
                                                    <Form.Text>
                                                        Default widget styles
                                                        load first (scoped with{" "}
                                                        <code>
                                                            [data-claritick-instance]
                                                        </code>
                                                        ). Your rules come next
                                                        and override when they
                                                        target the same property.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                </Col>
                                    </>
                                )}
                            </Row>
                            ) : wizardStep === afterSubmitWizardStep ? (
                            <Row className="g-3">
                                <Col xs={12}>
                                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                                        <h2 className="embed-form-section-title mb-0">
                                            After submit
                                        </h2>
                                        <Button
                                            type="button"
                                            variant={EmbedFormBtn.secondary}
                                            className="flex-shrink-0"
                                            onClick={() =>
                                                setAfterSubmitPreviewModalOpen(
                                                    true
                                                )
                                            }
                                        >
                                            Preview
                                        </Button>
                                    </div>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label>
                                            <RequiredFieldLabel>
                                                Success message
                                            </RequiredFieldLabel>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            value={successMessage}
                                            onChange={(e) =>
                                                setSuccessMessage(
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                "Thank you for contacting support!"
                                            }
                                            required
                                        />
                                        <Form.Text>
                                            Write the full message visitors see
                                            after submitting. Use Preview for the
                                            fullscreen success screen, or the
                                            Design step to style colors and fonts.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label>Disclaimer</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={successDisclaimer}
                                            onChange={(e) =>
                                                setSuccessDisclaimer(
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <Form.Text>
                                            Separate from the success message
                                            above. Shown at 16px under the form
                                            title, or under the success message if
                                            there is no title. Clear this field to
                                            hide it.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Contact{" "}
                                            <span className="text-muted fw-normal">
                                                (optional)
                                            </span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={supportEmail}
                                            onChange={(e) =>
                                                setSupportEmail(e.target.value)
                                            }
                                            placeholder="Email, phone, or link"
                                        />
                                        <Form.Text>
                                            Shown above Track your request on
                                            the success screen. Opens as email,
                                            phone call, or web link automatically.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label>
                                            Support notification emails
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={notifyEmailsText}
                                            onChange={(e) =>
                                                setNotifyEmailsText(
                                                    e.target.value
                                                )
                                            }
                                            placeholder={
                                                "support@company.com\nbilling@company.com"
                                            }
                                        />
                                        <Form.Text>
                                            Each line is a recipient when a
                                            ticket is created from this widget
                                            (in addition to normal Claritick
                                            notifications). This does not assign
                                            tickets to those users.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                            ) : wizardStep === autoAssignWizardStep ? (
                            <Row className="g-3">
                                <Col xs={12}>
                                    <h2 className="embed-form-section-title">
                                        Auto-Assign
                                    </h2>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label>
                                            Assign tickets to
                                        </Form.Label>
                                        <Select
                                            isMulti
                                            isClearable
                                            closeMenuOnSelect={false}
                                            options={autoAssignUserOptions}
                                            value={autoAssignSelectValue}
                                            onChange={(selected) =>
                                                setAutoAssignUserIds(
                                                    (selected || []).map(
                                                        (option) =>
                                                            Number(option.value)
                                                    )
                                                )
                                            }
                                            components={{ Option: imageOption }}
                                            classNamePrefix="select-component"
                                            placeholder="Select workspace users…"
                                        />
                                        {invalidAutoAssignUserIds.length >
                                        0 ? (
                                            <Alert
                                                variant="warning"
                                                className="mt-2 mb-0"
                                            >
                                                {invalidAutoAssignUserIds.length ===
                                                1
                                                    ? "User #"
                                                    : "Users #"}
                                                {invalidAutoAssignUserIds.join(
                                                    ", "
                                                )}{" "}
                                                {invalidAutoAssignUserIds.length ===
                                                1
                                                    ? "is no longer an active workspace member and will be removed when you save."
                                                    : "are no longer active workspace members and will be removed when you save."}
                                            </Alert>
                                        ) : null}
                                        <Form.Text>
                                            When a customer submits this form,
                                            the new ticket is assigned only to
                                            the users you select here. Leave
                                            empty to create unassigned tickets.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                            ) : wizardStep === 2 ? (
                            <>
                            <Row className="mt-2">
                                <Col className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h2 className="embed-form-section-title mb-0">
                                            Custom fields
                                        </h2>
                                        {customFields.length > 1 ? (
                                            <small className="text-muted">
                                                Drag rows to reorder fields.
                                            </small>
                                        ) : null}
                                    </div>
                                    <Button
                                        type="button"
                                        variant={EmbedFormBtn.secondary}
                                        size="sm"
                                        onClick={addField}
                                    >
                                        <BsPlusLg className="me-1" />
                                        Add field
                                    </Button>
                                </Col>
                            </Row>

                            <Row className="mt-2">
                                <Col className="overflow-x-auto">
                                    <Table
                                        size="sm"
                                        bordered
                                        className="embed-form-custom-fields-table"
                                    >
                                        <thead>
                                            <tr>
                                                <th
                                                    className="embed-form-custom-fields-drag-col"
                                                    aria-label="Reorder"
                                                >
                                                    <span className="visually-hidden">
                                                        Reorder
                                                    </span>
                                                </th>
                                                <th>
                                                    <RequiredFieldLabel>
                                                        Label
                                                    </RequiredFieldLabel>
                                                </th>
                                                <th>Type</th>
                                                <th className="embed-form-custom-fields-required-col">
                                                    Required
                                                </th>
                                                <th>Placeholder</th>
                                                <th className="embed-form-custom-fields-options-col">
                                                    Options
                                                </th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {customFields.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={7}
                                                        className="text-muted"
                                                    >
                                                        No custom fields (full
                                                        name, email and message
                                                        are always included).
                                                    </td>
                                                </tr>
                                            ) : (
                                                customFields.map((f, idx) => (
                                                    <tr
                                                        key={
                                                            f._clientId ??
                                                            idx
                                                        }
                                                        className={
                                                            dragOverFieldIndex ===
                                                            idx
                                                                ? "embed-form-custom-fields-row--drag-over"
                                                                : undefined
                                                        }
                                                        onDragOver={(ev) =>
                                                            handleFieldDragOver(
                                                                ev,
                                                                idx
                                                            )
                                                        }
                                                        onDragLeave={(ev) =>
                                                            handleFieldDragLeave(
                                                                ev,
                                                                idx
                                                            )
                                                        }
                                                        onDrop={(ev) =>
                                                            handleFieldDrop(
                                                                ev,
                                                                idx
                                                            )
                                                        }
                                                    >
                                                        <td className="embed-form-custom-fields-drag-col">
                                                            <button
                                                                type="button"
                                                                className="embed-form-custom-fields-drag-handle"
                                                                draggable
                                                                aria-label={`Drag to reorder field ${idx + 1}`}
                                                                title="Drag to reorder"
                                                                onDragStart={(
                                                                    ev
                                                                ) =>
                                                                    handleFieldDragStart(
                                                                        ev,
                                                                        idx
                                                                    )
                                                                }
                                                                onDragEnd={
                                                                    handleFieldDragEnd
                                                                }
                                                            >
                                                                <BsGripVertical
                                                                    aria-hidden
                                                                />
                                                            </button>
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                size="sm"
                                                                value={
                                                                    f.label
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) =>
                                                                    updateField(
                                                                        idx,
                                                                        {
                                                                            label: e
                                                                                .target
                                                                                .value,
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <Form.Select
                                                                size="sm"
                                                                value={
                                                                    f.type
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    const type =
                                                                        e
                                                                            .target
                                                                            .value;
                                                                    const patch =
                                                                        {
                                                                            type,
                                                                            ...(type ===
                                                                            "birthday"
                                                                                ? {
                                                                                      placeholder:
                                                                                          "",
                                                                                  }
                                                                                : {}),
                                                                        };
                                                                    if (
                                                                        embedFieldTypeUsesOptionsLayout(
                                                                            type
                                                                        ) &&
                                                                        !embedFieldTypeUsesOptionsLayout(
                                                                            f.type
                                                                        )
                                                                    ) {
                                                                        patch.options_layout =
                                                                            "vertical";
                                                                    }
                                                                    updateField(
                                                                        idx,
                                                                        patch
                                                                    );
                                                                }}
                                                            >
                                                                <option value="text">
                                                                    Text
                                                                </option>
                                                                <option value="textarea">
                                                                    Textarea
                                                                </option>
                                                                <option value="number">
                                                                    Number
                                                                </option>
                                                                <option value="birthday">
                                                                    Birthday
                                                                </option>
                                                                <option value="select">
                                                                    Select
                                                                </option>
                                                                <option value="radio">
                                                                    Radio
                                                                </option>
                                                                <option value="checkbox">
                                                                    Checkbox
                                                                </option>
                                                            </Form.Select>
                                                        </td>
                                                        <td className="embed-form-custom-fields-required-col">
                                                            <Form.Check
                                                                type="checkbox"
                                                                className="embed-form-custom-fields-required-check"
                                                                checked={
                                                                    f.required
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) =>
                                                                    updateField(
                                                                        idx,
                                                                        {
                                                                            required:
                                                                                e
                                                                                    .target
                                                                                    .checked,
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td
                                                            className={
                                                                embedFieldTypeShowsPlaceholderInput(
                                                                    f.type
                                                                )
                                                                    ? undefined
                                                                    : "embed-form-custom-fields-cell--na"
                                                            }
                                                            title={
                                                                embedFieldTypeShowsPlaceholderInput(
                                                                    f.type
                                                                )
                                                                    ? undefined
                                                                    : `Not applicable for ${f.type} fields`
                                                            }
                                                        >
                                                            {embedFieldTypeShowsPlaceholderInput(
                                                                f.type
                                                            ) ? (
                                                                <Form.Control
                                                                    size="sm"
                                                                    value={
                                                                        f.placeholder
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        updateField(
                                                                            idx,
                                                                            {
                                                                                placeholder:
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                            }
                                                                        )
                                                                    }
                                                                    placeholder={
                                                                        f.type ===
                                                                        "select"
                                                                            ? ""
                                                                            : undefined
                                                                    }
                                                                />
                                                            ) : (
                                                                <span
                                                                    className="embed-form-custom-fields-cell__na-mark"
                                                                    aria-label="Not applicable"
                                                                >
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td
                                                            className={
                                                                embedFieldTypeUsesOptions(
                                                                    f.type
                                                                )
                                                                    ? "embed-form-custom-fields-options-col"
                                                                    : "embed-form-custom-fields-options-col embed-form-custom-fields-cell--na"
                                                            }
                                                            title={
                                                                embedFieldTypeUsesOptions(
                                                                    f.type
                                                                )
                                                                    ? undefined
                                                                    : `Options are only used for select, radio, or checkbox fields`
                                                            }
                                                        >
                                                            {embedFieldTypeUsesOptions(
                                                                f.type
                                                            ) ? (
                                                                <FieldOptionsEditor
                                                                    fieldType={
                                                                        f.type
                                                                    }
                                                                    options={
                                                                        f.options ||
                                                                        []
                                                                    }
                                                                    optionsLayout={
                                                                        f.options_layout
                                                                    }
                                                                    onChange={(
                                                                        opts
                                                                    ) =>
                                                                        updateField(
                                                                            idx,
                                                                            {
                                                                                options:
                                                                                    opts,
                                                                            }
                                                                        )
                                                                    }
                                                                    onOptionsLayoutChange={(
                                                                        options_layout
                                                                    ) =>
                                                                        updateField(
                                                                            idx,
                                                                            {
                                                                                options_layout,
                                                                            }
                                                                        )
                                                                    }
                                                                />
                                                            ) : (
                                                                <span
                                                                    className="embed-form-custom-fields-cell__na-mark"
                                                                    aria-label="Not applicable"
                                                                >
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                type="button"
                                                                variant="tertiary"
                                                                className="small-icon-button danger"
                                                                onClick={() =>
                                                                    removeField(
                                                                        idx
                                                                    )
                                                                }
                                                            >
                                                                <BsTrash3 />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                            </>
                            ) : null}
                            </div>

                            <Row className="mt-4 align-items-center embed-form-editor-wizard-nav">
                                <Col
                                    xs={12}
                                    className="d-flex flex-wrap align-items-center justify-content-between gap-2"
                                >
                                    <div className="d-flex flex-wrap gap-2">
                                        {wizardStep > 0 ? (
                                            <Button
                                                type="button"
                                                variant={EmbedFormBtn.neutral}
                                                onClick={() =>
                                                    setWizardStep((s) =>
                                                        Math.max(0, s - 1)
                                                    )
                                                }
                                            >
                                                Back
                                            </Button>
                                        ) : null}
                                        <Button
                                            type="button"
                                            variant={EmbedFormBtn.danger}
                                            onClick={navigateToFormsList}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 ms-sm-auto">
                                        {showSaveButton ? (
                                            <Button
                                                type="submit"
                                                variant={
                                                    wizardStep ===
                                                    wizardLastIndex
                                                        ? EmbedFormBtn.primary
                                                        : EmbedFormBtn.secondary
                                                }
                                                disabled={
                                                    saving ||
                                                    (wizardStep ===
                                                        wizardLastIndex &&
                                                        !lastStepSaveEnabled)
                                                }
                                            >
                                                {saving ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                        {isNew &&
                                                        wizardStep ===
                                                            wizardLastIndex
                                                            ? "Finishing…"
                                                            : "Saving…"}
                                                    </>
                                                ) : isNew &&
                                                  wizardStep ===
                                                      wizardLastIndex ? (
                                                    "Finish"
                                                ) : (
                                                    "Save"
                                                )}
                                            </Button>
                                        ) : null}
                                        {wizardStep < wizardLastIndex ? (
                                            <Button
                                                type="button"
                                                variant={EmbedFormBtn.primary}
                                                onClick={wizardStepNext}
                                            >
                                                Next
                                            </Button>
                                        ) : null}
                                    </div>
                                </Col>
                            </Row>
                            </Form>
                        </Col>

                        <Col xl={5} lg={12} className="embed-form-editor-preview-col">
                            <div className="embed-form-editor-preview-sticky">
                                <div
                                    className={
                                        "embed-form-editor-preview-panel" +
                                        (displayMode ===
                                        EMBED_DISPLAY_MODE_FLOATING
                                            ? " embed-form-editor-preview-panel--floating"
                                            : "")
                                    }
                                >
                                    <EmbedFormEditorPreview
                                        cfg={previewCfg}
                                        showAfterSubmitPreview={
                                            afterSubmitWizardStep >= 0 &&
                                            wizardStep === afterSubmitWizardStep
                                        }
                                        openSuccessPreviewModal={
                                            afterSubmitPreviewModalOpen
                                        }
                                        onSuccessPreviewModalClose={() =>
                                            setAfterSubmitPreviewModalOpen(false)
                                        }
                                        allowViewToggle={
                                            designWizardStep >= 0 &&
                                            wizardStep === designWizardStep
                                        }
                                        designPreviewView={designPreviewView}
                                        onDesignPreviewViewChange={
                                            setDesignPreviewView
                                        }
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
            </Container>
            <EmbedHowToModal
                show={createdEmbedInfo != null}
                onHide={closeCreatedEmbedModalToList}
                variant="postCreate"
                formName={createdEmbedInfo?.name ?? ""}
                uuid={createdEmbedInfo?.uuid ?? null}
                formId={createdEmbedInfo?.id ?? null}
                displayMode={displayMode}
                onContinueEditing={goToEditCreatedForm}
                onGoToList={closeCreatedEmbedModalToList}
            />
            {dialogNodes}
            <EmbedFormConfirmModal
                show={leaveConfirmOpen}
                onHide={cancelLeavePage}
                title="Unsaved changes"
                message={EMBED_FORM_DISCARD_CONFIRM_MESSAGE}
                confirmLabel="Discard"
                cancelLabel="Stay"
                onConfirm={confirmLeavePage}
                closeOnConfirm={false}
                backdrop="static"
                keyboard={false}
            />
            <Modal
                show={showEmbedCssHelp}
                onHide={() => setShowEmbedCssHelp(false)}
                size="lg"
                centered
                className="embed-forms-ui"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Embed widget CSS hooks</Modal.Title>
                </Modal.Header>
                <Modal.Body className="small">
                    <p className="text-muted mb-3">
                        Defaults are injected as a <code>&lt;style&gt;</code> block first
                        (selectors are scoped with{" "}
                        <code>[data-claritick-instance]</code>
                        on the mount). Your Custom CSS is injected after, so it wins on
                        tie‑breakers. On live embeds that stylesheet is also appended to{" "}
                        <code>document.body</code> so it usually loads after the host
                        page&rsquo;s CSS. Class hooks are listed below.
                    </p>
                    <ul className="mb-0 ps-3 ps-sm-4">
                        <li className="mb-1">
                            <code>[data-claritick-instance]</code> — on the
                            outer mount (unique per embed)
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-root</code> — same
                            mount (class hook for embed hosts)
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-floating</code> — fixed
                            corner launcher shell; add{" "}
                            <code>.is-open</code> when the panel is visible
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-launcher</code> — round
                            button; <code>.claritick-ticket-embed-floating-panel</code>{" "}
                            — popup form
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-card</code> — panel
                            (background, border, radius)
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-title</code>,{" "}
                            <code>.claritick-ticket-embed-subtitle</code>
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-form</code>
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-label</code> — stacked
                            field wrappers
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-field</code> — inputs; modifiers:{" "}
                            <code>--text</code>, <code>--email</code>,{" "}
                            <code>--textarea</code>, <code>--number</code>,{" "}
                            <code>--birthday</code>,{" "}
                            <code>--select</code>, <code>--checkbox</code>,{" "}
                            <code>--radio</code>
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-option-group--checkbox</code>,{" "}
                            <code>.claritick-ticket-embed-checkbox-list</code>,{" "}
                            <code>.claritick-ticket-embed-option-row--checkbox</code>
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-option-group--radio</code>,{" "}
                            <code>.claritick-ticket-embed-radio-pills</code>,{" "}
                            <code>.claritick-ticket-embed-radio-pill</code>
                        </li>
                        <li className="mb-1">
                            <code>.claritick-ticket-embed-submit</code>,{" "}
                            <code>.claritick-ticket-embed-status</code>
                        </li>
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        type="button"
                        variant={EmbedFormBtn.primary}
                        onClick={() => setShowEmbedCssHelp(false)}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </MainContainer>
    );
};

export default EmbedTicketFormEditor;
